import React, { useRef, useState } from "react";
import type { UIEvent } from "react";
import type { SelectedCategoriesSlice } from "~/lib/prismic";
import type { SliceComponentProps } from "@prismicio/react";
import { asText } from "@prismicio/helpers";
import { PrismicImage } from "@prismicio/react";
import WvLink from "~/components/atoms/WvLink";
import { useLocalePath } from "@commercekit/remix";

const SelectedCategories = ({
  slice,
}: SliceComponentProps<SelectedCategoriesSlice>) => {
  const localePath = useLocalePath();

  const [activeCategoryIndex, setActiveCategoryIndex] = useState(-1);

  const [canScrollLeft, setCanScrollLeft] = useState(slice?.items?.length > 6);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "LEFT" | "RIGHT") => {
    const extent: number = direction === "LEFT" ? 300 : -300;
    containerRef?.current?.scrollBy({
      top: 0,
      left: extent,
      behavior: "smooth",
    });
  };

  const handleScroll = (e: UIEvent) => {
    const left_offset = e.currentTarget.scrollLeft + 1;
    const container_width = e.currentTarget.scrollWidth;
    const visible_width = e.currentTarget.clientWidth;

    const right_offset = container_width - (visible_width + left_offset);

    setCanScrollLeft(right_offset > 1);
    setCanScrollRight(left_offset > 1);
  };

  return (
    <div className="pt-3 mb-0 component-categories mb-lg-0 js-scroller">
      <div className="container">
        <div className="text-center component-categories__inner">
          <h2 className="pb-3 mb-3 heading underlined lg pb-lg-2 pb-lg-3">
            {asText(slice.primary.title)}
          </h2>
          <div className="px-2 row-scroller-lg js-row-scroller px-lg-4 no-gutters">
            <div
              className="mb-3 row justify-content-center justify-content-lg-start"
              ref={containerRef}
              onScroll={handleScroll}
            >
              {slice.items?.length
                ? slice.items.map((category, index) => (
                    <div
                      key={index}
                      onClick={() => setActiveCategoryIndex(index)}
                      className={`component-categoryblock ${
                        index == activeCategoryIndex ? "active" : ""
                      } col-4 col-md-3 col-lg-2 col-xl-custom mb-3 px-2 px-lg-3 js-scrollable`}
                    >
                      <WvLink
                        className="mx-auto d-block hexagon d-flex align-items-center justify-content-center"
                        to={localePath(`/${asText(category.slug)}`)}
                      >
                        <PrismicImage
                          field={category.category_image}
                          width="auto"
                          height="auto"
                        />
                      </WvLink>

                      <WvLink
                        to={localePath(`/${asText(category.slug)}`)}
                        className="mt-2 d-block"
                      >
                        <h3 className="heading xs strong sans-serif">
                          {asText(category.category_name)}
                        </h3>
                      </WvLink>
                    </div>
                  ))
                : null}
            </div>
          </div>
        </div>
        <div
          className={`component-categories-prev d-none d-lg-block js-scroller-prev ${
            canScrollRight ? "" : "disabled"
          }`}
          data-icon-before=""
          onClick={() => scroll("RIGHT")}
        ></div>
        <div
          className={`component-categories-next d-none d-lg-block js-scroller-next ${
            canScrollLeft ? "" : "disabled"
          }`}
          data-icon-before=""
          onClick={() => scroll("LEFT")}
        ></div>
      </div>
    </div>
  );
};

export default SelectedCategories;
